/**
 * Копия пешеходного маршрута из мобильного приложения
 * (packages/player/src/play/fetch-walking-route.ts).
 */
type LngLat = [number, number];

type ValhallaLeg = {
    shape?: string;
};

type ValhallaRouteResponse = {
    trip?: {
        status?: number;
        legs?: ValhallaLeg[];
    };
};

const STADIA_API_KEY = '0edec9b6-fb20-4e18-bfe2-fe2c35a01865';
const STADIA_ROUTE_URL = `https://api.stadiamaps.com/route/v1?api_key=${STADIA_API_KEY}`;

const cache = new Map<string, LngLat[]>();

const cacheKey = (points: LngLat[]): string =>
    `pedestrian:${points.map(([lng, lat]) => `${lng.toFixed(5)},${lat.toFixed(5)}`).join(';')}`;

/**
 * Декод Valhalla polyline6 → [lng, lat][].
 * @see https://github.com/valhalla/valhalla/blob/master/docs/docs/api/turn-by-turn/api-reference.md
 */
const decodePolyline6 = (encoded: string): LngLat[] => {
    const coordinates: LngLat[] = [];
    let index = 0;
    let lat = 0;
    let lng = 0;
    const factor = 1e6;

    while (index < encoded.length) {
        let result = 0;
        let shift = 0;
        let byte: number;

        do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;

        lat += deltaLat;

        result = 0;
        shift = 0;

        do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        const deltaLng = result & 1 ? ~(result >> 1) : result >> 1;

        lng += deltaLng;

        coordinates.push([lng / factor, lat / factor]);
    }

    return coordinates;
};

/**
 * Пешеходный маршрут по тротуарам/пешеходным путям (Stadia Valhalla, costing=pedestrian).
 * Координаты — [lng, lat]. При ошибке — null (caller рисует прямые).
 */
export const fetchWalkingRoute = async (points: LngLat[]): Promise<LngLat[] | null> => {
    if (points.length < 2) return null;

    const key = cacheKey(points);
    const cached = cache.get(key);

    if (cached) return cached;

    try {
        const response = await fetch(STADIA_ROUTE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                locations: points.map(([lng, lat]) => ({ lat, lon: lng })),
                costing: 'pedestrian',
                costing_options: {
                    pedestrian: {
                        walkway_factor: 0.9,
                        sidewalk_factor: 0.8,
                        alley_factor: 2,
                        driveway_factor: 5,
                        step_penalty: 0,
                        use_ferry: 0.5,
                        use_living_streets: 0.5,
                        use_tracks: 0.5,
                        use_hills: 0.4,
                    },
                },
                directions_options: { units: 'kilometers' },
            }),
        });

        if (!response.ok) return null;

        const json = (await response.json()) as ValhallaRouteResponse;
        const legs = json.trip?.legs;

        if (!legs?.length || json.trip?.status !== 0) return null;

        const coordinates: LngLat[] = [];

        for (const leg of legs) {
            if (!leg.shape) continue;
            const decoded = decodePolyline6(leg.shape);

            if (!decoded.length) continue;

            // стык ног: первая точка следующей ≈ последняя предыдущей
            if (coordinates.length && decoded.length) {
                coordinates.push(...decoded.slice(1));
            } else {
                coordinates.push(...decoded);
            }
        }

        if (coordinates.length < 2) return null;

        cache.set(key, coordinates);

        return coordinates;
    } catch {
        return null;
    }
};
